/**
 * Types for tenant-facing `/v1/auth/*` endpoints on platform-api (R1 scope).
 *
 * Differs from `/internal/*`: these contracts land on the public platform
 * surface (`platform.<env>.nadoutong.org`), authenticate with a signed
 * session cookie, and are designed for the console-web + developer CLI.
 *
 * Phase 1 realm:
 *   - Email + password only.
 *   - 1 account ↔ 1 org (ADR-003). Register creates both atomically.
 *   - OAuth providers listed in {@link AuthProvidersResponse.oauth} are
 *     stubbed empty; Phase 2 work lights them up.
 *
 * Error model mirrors the shared {@link ErrorEnvelope} convention; see
 * `errors.ts` for the canonical envelope + codes.
 */

import type { OrgMemberRole } from './public-orgs.ts';

export type TenantOrgPermission =
  | 'org:members:read'
  | 'org:members:manage'
  | 'org:invitations:manage'
  | 'org:api_keys:read'
  | 'org:api_keys:manage'
  | 'member:api_keys:manage'
  | 'org:billing:read'
  | 'org:billing:manage'
  | 'org:usage:read'
  | 'member:usage:read'
  | 'org:audit:read'
  | 'member:audit:read'
  | 'org:reports:export'
  | 'member:models:read'
  | 'member:monthly_limit:read'
  | 'member:requests:read';

/**
 * Closed list of `org:*` permissions a custom org role may combine
 * (tenant #178). Sourced from platform-api `permissionsForOrgRole`
 * manager constants — do not invent names here. `member:*` stays on
 * builtin sets only and is not a combinable option.
 *
 * `org:billing:read` may be granted without `org:billing:manage` on a
 * custom role. Builtin owner/admin sets still ship both together.
 */
export const CUSTOM_ORG_ROLE_PERMISSIONS = [
  'org:members:read',
  'org:members:manage',
  'org:invitations:manage',
  'org:api_keys:read',
  'org:api_keys:manage',
  'org:billing:read',
  'org:billing:manage',
  'org:usage:read',
  'org:audit:read',
  'org:reports:export',
] as const satisfies readonly TenantOrgPermission[];

export type CustomOrgRolePermission = (typeof CUSTOM_ORG_ROLE_PERMISSIONS)[number];

export const CUSTOM_ORG_ROLE_PERMISSION_SET: ReadonlySet<string> = new Set(
  CUSTOM_ORG_ROLE_PERMISSIONS,
);

export interface TenantOrgAccess {
  orgRole: OrgMemberRole;
  /**
   * Custom org role id when the member is hanging on a custom row;
   * `null` for the builtin owner/admin/member/finance constants.
   */
  customRoleId: string | null;
  permissions: readonly TenantOrgPermission[];
}

export interface RegisterRequest {
  email: string;
  password: string;
  /** Optional; defaults to local-part of email when omitted. */
  displayName?: string;
}

export interface RegisterResponse {
  accountId: string;
  orgId: string;
  /** Always true in Phase 1 — verification link is emailed on register. */
  verificationRequired: true;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accountId: string;
  orgId: string;
  emailVerified: boolean;
  orgRole: OrgMemberRole;
  customRoleId: string | null;
  permissions: readonly TenantOrgPermission[];
}

export interface LogoutResponse {
  ok: true;
}

export interface SessionResponse {
  accountId: string;
  orgId: string;
  email: string;
  emailVerified: boolean;
  /**
   * True when the account has a login password set. Invite-code activated
   * accounts are passwordless, so this is false. The console uses it to
   * decide whether to prompt for setting a password.
   */
  hasPassword: boolean;
  orgRole: OrgMemberRole;
  customRoleId: string | null;
  permissions: readonly TenantOrgPermission[];
}

/**
 * Body for `POST /v1/auth/verify-email`. The console-web verify-email route
 * pulls `?token=...` out of the email link and sends it as the request body
 * so the backend can mint a session cookie in the same round-trip — the
 * legacy `GET /v1/auth/verify-email` returns `{ verified: true }` only and
 * is kept alive to honour pre-existing email links until the deploy window
 * closes.
 */
export interface VerifyEmailRequest {
  token: string;
}

/**
 * Response for both the legacy `GET /v1/auth/verify-email` (verified:true
 * only, no session) and the new `POST /v1/auth/verify-email` which also
 * mints a session cookie and returns the tenant principal so the console
 * can redirect straight into /dashboard without a second login round-trip.
 */
export interface VerifyEmailResponse {
  verified: true;
  /**
   * Present only on the POST variant, absent on the GET variant. The GET
   * endpoint never mints a session — it stays side-effect-free so pre-fetchers
   * (email clients scanning link previews, anti-phishing scanners) do not
   * silently consume tokens.
   */
  accountId?: string;
  orgId?: string;
  emailVerified?: true;
  orgRole?: OrgMemberRole;
  customRoleId?: string | null;
  permissions?: readonly TenantOrgPermission[];
}

/**
 * Body for `POST /v1/auth/verify-email/resend`. Takes only the email — the
 * recipient is the authoritative identifier since the prior token may have
 * expired or been consumed by an anti-phishing scanner.
 */
export interface ResendVerificationRequest {
  email: string;
}

/**
 * Response is intentionally `{ ok: true }` regardless of whether the email
 * matches an unverified account — prevents enumeration (mirrors the
 * forgot-password ack shape). Callers must not rely on this body to learn
 * if the email exists.
 */
export interface ResendVerificationResponse {
  ok: true;
}

export interface ForgotPasswordRequest {
  email: string;
}

/**
 * Response is intentionally the same shape regardless of whether the email
 * exists — prevents account enumeration. Callers must not rely on this
 * body to distinguish "email sent" from "email not on file".
 */
export interface ForgotPasswordResponse {
  ok: true;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  ok: true;
}

/**
 * Authenticated (session-cookie) password change from the Settings page.
 *
 * Flow differs from {@link ResetPasswordRequest} in that there is no
 * out-of-band token — the caller proves liveness by supplying the current
 * password, which is re-verified against the stored scrypt hash. All
 * sessions (including the caller's) are revoked after a successful change,
 * so the client must sign in again.
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  ok: true;
}

export interface AuthProvidersResponse {
  /** Email + password provider — always true in Phase 1. */
  email: true;
  /** OAuth providers — empty array Phase 1; populated in Phase 2. */
  oauth: readonly string[];
}

/**
 * Body for `POST /v1/auth/activate` — invite-code activation (epic #1475).
 *
 * Creates a passwordless account (+ org, membership, zero balance), consumes
 * one slot of the code's UTC+8 daily quota, issues the trial `gift_direct`
 * credit lot, and mints a session cookie — all in one transaction.
 * Deliberately performs NO email-ownership verification: the daily quota is
 * the abuse boundary (an activation with someone else's email wastes a slot
 * but cannot log in, since login codes go to the real mailbox).
 */
export interface ActivateRequest {
  email: string;
  inviteCode: string;
}

export interface ActivateResponse {
  accountId: string;
  /**
   * Trial grant snapshot for the success view. Face value is CNY cents as
   * configured at activation time; clients render these fields verbatim and
   * must not recompute amount or expiry locally.
   */
  grant: {
    amountCnyCents: number;
    expiresAt: string;
  };
}

/**
 * Body for `POST /v1/auth/login/code/request` — email login-code issuance.
 * Responds 202 with the same shape whether or not the email is registered;
 * unknown/suspended/deleted accounts produce no side effects (no email, no
 * code row) but an identical response, so the endpoint is not an account
 * enumeration oracle. Resend interval and daily cap are enforced uniformly
 * for ALL emails via a hashed-email throttle for the same reason.
 */
export interface LoginCodeRequestRequest {
  email: string;
}

export interface LoginCodeRequestResponse {
  /** Login-code TTL in seconds (runtime-configured; launch value 600). */
  expiresInSeconds: number;
}

/**
 * Body for `POST /v1/auth/login/code/verify` — code check + session mint.
 * Open to every account: the only login path for passwordless (invite-
 * activated) accounts and an additional path for password accounts. A
 * successful verify also marks the account email as verified — possessing
 * the emailed code is exactly the proof the verification link establishes.
 */
export interface LoginCodeVerifyRequest {
  email: string;
  code: string;
}

export interface LoginCodeVerifyResponse {
  accountId: string;
}

/**
 * Success body for `POST /v1/auth/password/set/code`. Session-authenticated
 * (tenant cookie), no request body — the code is emailed to the calling
 * account's own address, so there is no Request type (same as logout). Only
 * available to accounts without a password ({@link SessionResponse.hasPassword}
 * === false); accounts that already have one receive 409
 * `PASSWORD_ALREADY_SET`. Sends a single-use 6-digit code; `expiresInSeconds`
 * is the fixed code TTL (600). Requesting a new code retires any previous
 * open one. Resend is limited to 1/min per account plus a rolling 24h cap.
 */
export interface SetPasswordCodeResponse {
  expiresInSeconds: number;
}

/**
 * Body for `POST /v1/auth/password/set`. `code` is the 6-digit emailed code;
 * `newPassword` must be 10-128 chars. Reuses the login-code error vocabulary:
 * `CODE_INVALID` (wrong/malformed/unknown), `CODE_EXPIRED`,
 * `CODE_RATE_LIMITED` (attempt cap exhausted or verify-side rate limit). 409
 * `PASSWORD_ALREADY_SET` when a password already exists — including one set
 * concurrently via the reset flow; the check is re-run inside the transaction.
 * On success every session EXCEPT the calling one is revoked. This
 * deliberately diverges from `/password/change` and `/password/reset` (which
 * revoke all sessions): the caller has just proven mailbox control in-session,
 * and the flow exists precisely so they do not get signed out.
 */
export interface SetPasswordRequest {
  code: string;
  newPassword: string;
}

/** `{ ok: true }` ack for `POST /v1/auth/password/set`. */
export interface SetPasswordResponse {
  ok: true;
}
